// src/components/RouteRiskHeatmap.js

import { useMemo, useState } from 'react';
import { MapPin, AlertTriangle, ArrowRight, TrendingUp } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const STATE_COORDS = {
  'MA': { x: 520, y: 120, name: 'Massachusetts' },
  'MI': { x: 380, y: 140, name: 'Michigan' },
  'PA': { x: 470, y: 160, name: 'Pennsylvania' },
  'IL': { x: 350, y: 170, name: 'Illinois' },
  'NC': { x: 480, y: 230, name: 'North Carolina' },
  'OH': { x: 420, y: 170, name: 'Ohio' },
  'MN': { x: 310, y: 100, name: 'Minnesota' },
  'MD': { x: 485, y: 180, name: 'Maryland' },
  'CA': { x: 80, y: 200, name: 'California' },
  'NY': { x: 500, y: 130, name: 'New York' },
  'TN': { x: 400, y: 230, name: 'Tennessee' },
  'IN': { x: 390, y: 180, name: 'Indiana' },
};

function RouteRiskHeatmap({ shipments, excursions }) {
  const { colors } = useTheme();
  const [selectedRoute, setSelectedRoute] = useState(null);
  
  const getRiskColor = (rate) => rate >= 40 ? colors.danger : rate >= 25 ? colors.warning : colors.success;
  
  const routeStats = useMemo(() => {
    const routes = {};
    shipments.forEach(s => {
      const routeKey = `${s.originState}-${s.destinationState}`;
      if (!routes[routeKey]) routes[routeKey] = { originState: s.originState, destinationState: s.destinationState, totalShipments: 0, excursions: 0, critical: 0, totalTransitHours: 0, totalValue: 0 };
      routes[routeKey].totalShipments++;
      routes[routeKey].totalTransitHours += s.transitHours || 0;
      routes[routeKey].totalValue += s.totalValue || 0;
      if (s.hasExcursion) {
        routes[routeKey].excursions++;
        if (s.excursionSeverity === 'Critical') routes[routeKey].critical++;
      }
    });
    return Object.values(routes).map(r => ({ ...r, excursionRate: (r.excursions / r.totalShipments) * 100, avgTransitHours: r.totalTransitHours / r.totalShipments, routeKey: `${r.originState}-${r.destinationState}` })).sort((a, b) => b.excursionRate - a.excursionRate);
  }, [shipments]);
  
  const selectedRouteDetail = useMemo(() => {
    if (!selectedRoute) return null;
    const routeShipments = shipments.filter(s => s.originState === selectedRoute.originState && s.destinationState === selectedRoute.destinationState);
    const routeExcursions = excursions.filter(e => {
      const shipment = shipments.find(s => s.id === e.shipmentId);
      return shipment && shipment.originState === selectedRoute.originState && shipment.destinationState === selectedRoute.destinationState;
    });
    const carrierStats = {};
    routeShipments.forEach(s => {
      const carrier = s.carrierName?.split(' ')[0] || 'Unknown';
      if (!carrierStats[carrier]) carrierStats[carrier] = { total: 0, excursions: 0 };
      carrierStats[carrier].total++;
      if (s.hasExcursion) carrierStats[carrier].excursions++;
    });
    const rootCauses = {};
    routeExcursions.forEach(e => rootCauses[e.rootCause] = (rootCauses[e.rootCause] || 0) + 1);
    return {
      ...selectedRoute,
      carriers: Object.entries(carrierStats).map(([name, data]) => ({ name, total: data.total, rate: ((data.excursions / data.total) * 100).toFixed(0) })).sort((a, b) => parseFloat(b.rate) - parseFloat(a.rate)),
      rootCauses: Object.entries(rootCauses).map(([cause, count]) => ({ cause, count })).sort((a, b) => b.count - a.count).slice(0, 3),
    };
  }, [selectedRoute, shipments, excursions]);

  const RouteCard = ({ route }) => {
    const riskColor = getRiskColor(route.excursionRate);
    const isSelected = selectedRoute?.routeKey === route.routeKey;
    return (
      <div onClick={() => setSelectedRoute(isSelected ? null : route)} style={{ background: isSelected ? colors.bgElevated : colors.bgCard, border: `1px solid ${isSelected ? riskColor : colors.border}`, borderRadius: '12px', padding: '1rem', cursor: 'pointer', transition: 'all 0.2s ease', marginBottom: '0.75rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', fontWeight: 500, color: colors.textPrimary }}>
            <span>{route.originState}</span><ArrowRight size={14} color={colors.textMuted} /><span>{route.destinationState}</span>
          </div>
          <div style={{ fontSize: '1rem', fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", color: riskColor }}>{route.excursionRate.toFixed(0)}%</div>
        </div>
        <div style={{ display: 'flex', gap: '1rem', fontSize: '0.75rem', color: colors.textMuted }}>
          <span>{route.totalShipments} shipments</span><span>{route.excursions} excursions</span><span>{route.avgTransitHours.toFixed(0)}h avg transit</span>
        </div>
      </div>
    );
  };

  const RouteLine = ({ x1, y1, x2, y2, excursionRate, isHighlighted }) => {
    const color = getRiskColor(excursionRate);
    const midX = (x1 + x2) / 2, midY = (y1 + y2) / 2;
    const dx = x2 - x1, dy = y2 - y1, dist = Math.sqrt(dx * dx + dy * dy);
    const curveOffset = dist * 0.15;
    const ctrlX = midX + (-dy / dist * curveOffset), ctrlY = midY + (dx / dist * curveOffset);
    return (
      <g>
        {isHighlighted && <path d={`M ${x1} ${y1} Q ${ctrlX} ${ctrlY} ${x2} ${y2}`} fill="none" stroke={color} strokeWidth={8} opacity={0.2} strokeLinecap="round" />}
        <path d={`M ${x1} ${y1} Q ${ctrlX} ${ctrlY} ${x2} ${y2}`} fill="none" stroke={color} strokeWidth={isHighlighted ? 3 : 1.5} opacity={isHighlighted ? 1 : 0.3} strokeLinecap="round" strokeDasharray={isHighlighted ? "none" : "4 4"} />
        {isHighlighted && <circle r={4} fill={color}><animateMotion dur="2s" repeatCount="indefinite" path={`M ${x1} ${y1} Q ${ctrlX} ${ctrlY} ${x2} ${y2}`} /></circle>}
      </g>
    );
  };

  const LocationNode = ({ x, y, state, isOrigin, isDestination, isHighlighted }) => {
    const size = isHighlighted ? 16 : 12;
    const color = isHighlighted ? (isOrigin ? colors.success : colors.info) : colors.textMuted;
    return (
      <g>
        {isHighlighted && <circle cx={x} cy={y} r={size + 8} fill={color} opacity={0.2} />}
        <circle cx={x} cy={y} r={size} fill={colors.bgCard} stroke={color} strokeWidth={isHighlighted ? 3 : 2} />
        <circle cx={x} cy={y} r={size / 3} fill={color} />
        <text x={x} y={y - size - 8} textAnchor="middle" fill={isHighlighted ? colors.textPrimary : colors.textMuted} fontSize={isHighlighted ? 12 : 10} fontWeight={isHighlighted ? 600 : 400} fontFamily="'DM Sans', sans-serif">{state}</text>
      </g>
    );
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 350px', gap: '1.5rem' }}>
      <div style={{ background: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: '16px', overflow: 'hidden' }}>
        <div style={{ padding: '1rem 1.25rem', borderBottom: `1px solid ${colors.border}`, display: 'flex', alignItems: 'center', gap: '0.5rem', background: colors.bgSecondary }}>
          <MapPin size={18} color={colors.primary} /><span style={{ fontSize: '0.9rem', fontWeight: 600, color: colors.textPrimary }}>Route Risk Map</span>
        </div>
        <div style={{ padding: '1rem' }}>
          <div style={{ width: '100%', height: '350px', background: colors.bgSecondary, borderRadius: '12px' }}>
            <svg width="100%" height="100%" viewBox="0 0 600 350">
              <rect width="600" height="350" fill={colors.bgSecondary} />
              {[...Array(6)].map((_, i) => <line key={`h-${i}`} x1={0} y1={i * 70} x2={600} y2={i * 70} stroke={colors.chartGrid} strokeWidth={1} />)}
              {[...Array(9)].map((_, i) => <line key={`v-${i}`} x1={i * 75} y1={0} x2={i * 75} y2={350} stroke={colors.chartGrid} strokeWidth={1} />)}
              {routeStats.map(route => {
                const origin = STATE_COORDS[route.originState], dest = STATE_COORDS[route.destinationState];
                if (!origin || !dest) return null;
                return <RouteLine key={route.routeKey} x1={origin.x} y1={origin.y} x2={dest.x} y2={dest.y} excursionRate={route.excursionRate} isHighlighted={selectedRoute?.routeKey === route.routeKey} />;
              })}
              {Object.entries(STATE_COORDS).map(([state, coords]) => (
                <LocationNode key={state} x={coords.x} y={coords.y} state={state} isOrigin={selectedRoute?.originState === state} isDestination={selectedRoute?.destinationState === state} isHighlighted={selectedRoute?.originState === state || selectedRoute?.destinationState === state} />
              ))}
            </svg>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '1.5rem', marginTop: '1rem', fontSize: '0.75rem', color: colors.textSecondary }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}><div style={{ width: '10px', height: '10px', borderRadius: '50%', background: colors.danger }} /><span>High Risk (40%+)</span></div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}><div style={{ width: '10px', height: '10px', borderRadius: '50%', background: colors.warning }} /><span>Medium Risk (25-40%)</span></div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}><div style={{ width: '10px', height: '10px', borderRadius: '50%', background: colors.success }} /><span>Low Risk (&lt;25%)</span></div>
          </div>
        </div>
      </div>
      
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        <div style={{ background: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: '16px', overflow: 'hidden' }}>
          <div style={{ padding: '1rem 1.25rem', borderBottom: `1px solid ${colors.border}`, display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', fontWeight: 600, color: colors.textPrimary, background: colors.bgSecondary }}>
            <AlertTriangle size={18} color={colors.warning} />High Risk Routes
          </div>
          <div style={{ padding: '1rem 1.25rem', maxHeight: '300px', overflowY: 'auto' }}>
            {routeStats.slice(0, 8).map(route => <RouteCard key={route.routeKey} route={route} />)}
          </div>
        </div>
        
        <div style={{ background: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: '16px', overflow: 'hidden' }}>
          <div style={{ padding: '1rem 1.25rem', borderBottom: `1px solid ${colors.border}`, display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', fontWeight: 600, color: colors.textPrimary, background: colors.bgSecondary }}>
            <TrendingUp size={18} color={colors.info} />Route Details
          </div>
          <div style={{ padding: '1rem 1.25rem' }}>
            {selectedRouteDetail ? (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1.1rem', fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' }}>
                  <span>{selectedRouteDetail.originState}</span><ArrowRight size={16} color={colors.textMuted} /><span>{selectedRouteDetail.destinationState}</span>
                  <span style={{ marginLeft: 'auto', color: getRiskColor(selectedRouteDetail.excursionRate), fontFamily: "'JetBrains Mono', monospace" }}>{selectedRouteDetail.excursionRate.toFixed(0)}%</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                  <div><div style={{ fontSize: '0.7rem', color: colors.textMuted, textTransform: 'uppercase', marginBottom: '0.25rem' }}>Shipments</div><div style={{ color: colors.textPrimary }}>{selectedRouteDetail.totalShipments}</div></div>
                  <div><div style={{ fontSize: '0.7rem', color: colors.textMuted, textTransform: 'uppercase', marginBottom: '0.25rem' }}>Excursions</div><div style={{ color: colors.textPrimary }}>{selectedRouteDetail.excursions}</div></div>
                  <div><div style={{ fontSize: '0.7rem', color: colors.textMuted, textTransform: 'uppercase', marginBottom: '0.25rem' }}>Avg Transit</div><div style={{ color: colors.textPrimary }}>{selectedRouteDetail.avgTransitHours.toFixed(0)}h</div></div>
                  <div><div style={{ fontSize: '0.7rem', color: colors.textMuted, textTransform: 'uppercase', marginBottom: '0.25rem' }}>Critical</div><div style={{ color: colors.danger }}>{selectedRouteDetail.critical}</div></div>
                </div>
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ fontSize: '0.7rem', color: colors.textMuted, textTransform: 'uppercase', marginBottom: '0.5rem' }}>Carrier Performance</div>
                  {selectedRouteDetail.carriers.map(c => (
                    <div key={c.name} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0', borderBottom: `1px solid ${colors.border}` }}>
                      <span style={{ fontSize: '0.8rem', color: colors.textSecondary }}>{c.name}</span>
                      <span style={{ fontSize: '0.8rem', fontWeight: 600, fontFamily: "'JetBrains Mono', monospace", color: getRiskColor(parseFloat(c.rate)) }}>{c.rate}%</span>
                    </div>
                  ))}
                </div>
                {selectedRouteDetail.rootCauses.length > 0 && (
                  <div>
                    <div style={{ fontSize: '0.7rem', color: colors.textMuted, textTransform: 'uppercase', marginBottom: '0.5rem' }}>Top Root Causes</div>
                    {selectedRouteDetail.rootCauses.map((rc, i) => <div key={rc.cause} style={{ fontSize: '0.8rem', color: colors.textSecondary, padding: '0.35rem 0' }}>{i + 1}. {rc.cause} ({rc.count})</div>)}
                  </div>
                )}
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '2rem', color: colors.textMuted }}>
                <MapPin size={32} color={colors.textMuted} style={{ marginBottom: '0.5rem' }} /><div>Select a route to view details</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default RouteRiskHeatmap;